import { Link } from "react-router-dom";
import { Heart, Phone, Mail, MapPin, Instagram } from "lucide-react";
import { NewsletterSignup } from "./NewsletterSignup";
import { BUSINESS_INFO, getPhoneLink } from "@/lib/constants";
import { trackNavigation, trackPhoneClick, trackEmailClick, trackSocialClick } from "@/lib/analytics";

const Footer = () => {
  return (
    <footer className="bg-secondary/50 border-t">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Contact - NAP Consistent */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Heart className="h-8 w-8 text-primary" fill="currentColor" />
              <span className="text-xl font-bold text-gradient-hero">
                {BUSINESS_INFO.name}
              </span>
            </div>
            <address className="not-italic space-y-2">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Phone className="h-4 w-4" aria-hidden="true" />
                <a 
                  href={getPhoneLink('tel')} 
                  className="hover:text-primary transition-colors"
                  aria-label={`Call ${BUSINESS_INFO.name} at ${BUSINESS_INFO.phone.display}`}
                  onClick={() => trackPhoneClick('footer')}
                >
                  {BUSINESS_INFO.phone.display}
                </a>
              </div>
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Mail className="h-4 w-4" aria-hidden="true" />
                <a 
                  href={`mailto:${BUSINESS_INFO.email}`}
                  className="hover:text-primary transition-colors"
                  onClick={() => trackEmailClick('footer')}
                >
                  {BUSINESS_INFO.email}
                </a>
              </div>
              <div className="flex items-center space-x-2 text-muted-foreground">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                <span>{BUSINESS_INFO.address.full}</span>
              </div>
            </address>
          </div>

          {/* Services - Internal Linking Cluster */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Mobile Dog & Cat Grooming Services</h3>
            <nav aria-label="Service pages" className="space-y-2">
              <Link to="/services" onClick={() => trackNavigation('All Services', 'footer_services', '/services')} className="block text-muted-foreground hover:text-primary transition-colors">
                All Grooming Services
              </Link>
              <Link to="/services/full-groom" onClick={() => trackNavigation('Full Groom', 'footer_services', '/services/full-groom')} className="block text-muted-foreground hover:text-primary transition-colors">
                Full Groom – Mobile Dog Haircuts & Styling
              </Link>
              <Link to="/services/spa-bath" onClick={() => trackNavigation('Spa Bath', 'footer_services', '/services/spa-bath')} className="block text-muted-foreground hover:text-primary transition-colors">
                Spa Bath & Brush Package
              </Link>
              <Link to="/services/cat-grooming" onClick={() => trackNavigation('Cat Grooming', 'footer_services', '/services/cat-grooming')} className="block text-muted-foreground hover:text-primary transition-colors">
                Cat Grooming – Lion Cuts & Mat Removal
              </Link>
              <Link to="/services/deshedding" onClick={() => trackNavigation('Deshedding', 'footer_services', '/services/deshedding')} className="block text-muted-foreground hover:text-primary transition-colors">
                Deshedding Treatment – Reduce Shedding 80%
              </Link>
              <Link to="/pricing" onClick={() => trackNavigation('Pricing', 'footer_services', '/pricing')} className="block text-muted-foreground hover:text-primary transition-colors">
                Denver Grooming Prices – Transparent Pricing
              </Link>
              <Link to="/booking" onClick={() => trackNavigation('Book Appointment', 'footer_services', '/booking')} className="block text-primary font-medium hover:underline transition-colors">
                Book Your Appointment →
              </Link>
            </nav>
          </div>

          {/* Locations - Internal Linking Cluster */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Denver Neighborhoods We Serve</h3>
            <nav aria-label="Location pages" className="space-y-2">
              <Link to="/locations" onClick={() => trackNavigation('All Locations', 'footer_locations', '/locations')} className="block text-muted-foreground hover:text-primary transition-colors">
                All Denver Service Areas
              </Link>
              <Link to="/locations/cherry-creek" onClick={() => trackNavigation('Cherry Creek', 'footer_locations', '/locations/cherry-creek')} className="block text-muted-foreground hover:text-primary transition-colors">
                Mobile Grooming in Cherry Creek
              </Link>
              <Link to="/locations/highlands-ranch" onClick={() => trackNavigation('Highlands Ranch', 'footer_locations', '/locations/highlands-ranch')} className="block text-muted-foreground hover:text-primary transition-colors">
                Mobile Grooming in Highlands Ranch
              </Link>
              <Link to="/locations/capitol-hill" onClick={() => trackNavigation('Capitol Hill', 'footer_locations', '/locations/capitol-hill')} className="block text-muted-foreground hover:text-primary transition-colors">
                Mobile Grooming in Capitol Hill
              </Link>
              <Link to="/locations/aurora" onClick={() => trackNavigation('Aurora', 'footer_locations', '/locations/aurora')} className="block text-muted-foreground hover:text-primary transition-colors">
                Mobile Pet Spa in Aurora
              </Link>
              <Link to="/locations/wash-park" onClick={() => trackNavigation('Wash Park', 'footer_locations', '/locations/wash-park')} className="block text-muted-foreground hover:text-primary transition-colors">
                Mobile Grooming in Washington Park
              </Link>
              <Link to="/booking" onClick={() => trackNavigation('Book in Area', 'footer_locations', '/booking')} className="block text-primary font-medium hover:underline transition-colors">
                Book in Your Area →
              </Link>
            </nav>
          </div>

          {/* Resources - Internal Linking Cluster */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Resources & Guides</h3>
            <nav aria-label="Resource pages" className="space-y-2">
              <Link to="/blog" onClick={() => trackNavigation('Blog', 'footer_resources', '/blog')} className="block text-muted-foreground hover:text-primary transition-colors">
                Dog & Cat Grooming Blog – Tips & Advice
              </Link>
              <Link to="/gallery" onClick={() => trackNavigation('Gallery', 'footer_resources', '/gallery')} className="block text-muted-foreground hover:text-primary transition-colors">
                Before & After Photos – Denver Grooming
              </Link>
              <Link to="/breeds/dogs" onClick={() => trackNavigation('Dog Breeds', 'footer_resources', '/breeds/dogs')} className="block text-muted-foreground hover:text-primary transition-colors">
                Dog Breed Grooming Guides
              </Link>
              <Link to="/breeds/cats" onClick={() => trackNavigation('Cat Breeds', 'footer_resources', '/breeds/cats')} className="block text-muted-foreground hover:text-primary transition-colors">
                Cat Breed Grooming Guides
              </Link>
              <Link to="/faq" onClick={() => trackNavigation('FAQ', 'footer_resources', '/faq')} className="block text-muted-foreground hover:text-primary transition-colors">
                Frequently Asked Questions
              </Link>
              <Link to="/loyalty" onClick={() => trackNavigation('Loyalty', 'footer_resources', '/loyalty')} className="block text-muted-foreground hover:text-primary transition-colors">
                Pawsome Rewards Program
              </Link>
            </nav>
          </div>

          {/* Newsletter Signup */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Stay Updated</h3>
            <NewsletterSignup source="footer" variant="compact" />
            <div className="flex space-x-4 mt-6">
              <a
                href={BUSINESS_INFO.social.instagram}
                className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Follow ${BUSINESS_INFO.name} on Instagram`}
                onClick={() => trackSocialClick('instagram', 'footer')}
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={BUSINESS_INFO.social.tiktok}
                className="p-2 rounded-full bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Follow ${BUSINESS_INFO.name} on TikTok`}
                onClick={() => trackSocialClick('tiktok', 'footer')}
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19.321 5.562a5.124 5.124 0 01-.443-.258 6.228 6.228 0 01-1.137-.966c-.849-.952-1.223-2.073-1.223-3.338V1h-3.27v14.26c0 2.024-1.644 3.668-3.668 3.668S6.912 17.284 6.912 15.26s1.644-3.668 3.668-3.668c.377 0 .741.057 1.086.163V8.49a7.925 7.925 0 00-1.086-.074c-4.124 0-7.668 3.544-7.668 7.668S6.456 23.752 10.58 23.752s7.668-3.544 7.668-7.668V9.721a9.13 9.13 0 004.494 1.157V7.611a5.152 5.152 0 01-3.421-2.049z"/>
                </svg>
              </a>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Tag us @groomieroomie to be featured as Pet of the Month! 📸
            </p>
          </div>
        </div>

        <div className="border-t border-border mt-8 pt-8 text-center text-muted-foreground text-sm">
          <nav aria-label="Legal pages" className="flex flex-wrap justify-center gap-4 mb-4">
            <Link to="/privacy-policy" onClick={() => trackNavigation('Privacy Policy', 'footer_legal', '/privacy-policy')} className="hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <span aria-hidden="true">|</span>
            <Link to="/cookie-policy" onClick={() => trackNavigation('Cookie Policy', 'footer_legal', '/cookie-policy')} className="hover:text-primary transition-colors">
              Cookie Policy
            </Link>
            <span aria-hidden="true">|</span>
            <Link to="/sitemap" onClick={() => trackNavigation('Sitemap', 'footer_legal', '/sitemap')} className="hover:text-primary transition-colors">
              Sitemap
            </Link>
          </nav>
          <p>&copy; {new Date().getFullYear()} {BUSINESS_INFO.legalName}. All rights reserved. Made with ❤️ for pets in Denver.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
